// components/ui.jsx — Shared corporate-engineering UI primitives for Enginuity
// Calm, photographic, professional. Like Hatch/DRA/EPCM.

'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { routeToUrl } from '@/lib/nav';
import { CAPABILITIES } from '@/lib/site-data';
import { getInsightBySlug, getCategoryBySlug } from '@/lib/insights';

// ── Logo ────────────────────────────────────────────────────────────────────
function Logo({ size = 32, variant = 'dark' }) {
  // Official Enginuity brand lockup (three bars + wordmark + tagline).
  // 'light' = white wordmark for dark backgrounds; 'dark' = steel-blue for light backgrounds;
  // 'black' = near-black wordmark for maximum contrast on light surfaces.
  // Files are 96px tall; light/dark are wider (with tagline) than black.
  const src = variant === 'light'
    ? '/assets/enginuity-logo-light.webp'
    : variant === 'black'
      ? '/assets/enginuity-logo-black.webp'
      : '/assets/enginuity-logo-dark.webp';
  const ratio = variant === 'black' ? 320 / 96 : 628 / 96;
  return (
    <img src={src}
         alt="Enginuity: Empowering innovation, engineering success"
         width={Math.round(size * ratio)} height={size}
         style={{ height: size, width: 'auto', display: 'block' }} />
  );
}

// ── Eyebrow — small label above titles ─────────────────────────────────────
function Eyebrow({ children, color, style = {} }) {
  return (
    <div style={{
      fontFamily: 'var(--ff-eyebrow)',
      fontSize: 13,
      fontWeight: 600,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: color || 'var(--accent)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 10,
      whiteSpace: 'nowrap',
      ...style,
    }}>
      <span style={{ display: 'inline-block', width: 24, height: 1, background: 'currentColor', flexShrink: 0 }} />
      {children}
    </div>
  );
}

// ── Headline ───────────────────────────────────────────────────────────────
function Headline({ children, size = 'lg', as = 'h2', style = {}, balance = true }) {
  const Tag = as;
  const sizes = {
    sm:   'clamp(22px, 2vw, 30px)',
    md:   'clamp(28px, 3vw, 42px)',
    lg:   'clamp(36px, 4.5vw, 58px)',
    xl:   'clamp(44px, 6vw, 80px)',
    xxl:  'clamp(54px, 8vw, 110px)',
  };
  const lh = {
    sm: 1.2, md: 1.15, lg: 1.08, xl: 1.04, xxl: 1.0,
  };
  return (
    <Tag style={{
      fontFamily: 'var(--ff-display)',
      fontWeight: 'var(--display-w)',
      letterSpacing: 'var(--display-track)',
      fontSize: sizes[size],
      lineHeight: lh[size],
      color: 'var(--fg)',
      margin: 0,
      textWrap: balance ? 'balance' : 'pretty',
      ...style,
    }}>{children}</Tag>
  );
}

// ── Photo placeholder — clean, no HUD chrome ───────────────────────────────
// Diagonal-stripe placeholder with a small mono caption corner that says what
// image goes here. Photo is the star — we don't decorate it.
function Photo({ caption, src, srcSm, objectPosition = 'center', aspect = '4/3', tone = 'industrial', kind = 'plant', style = {}, parallax = 0, overlay, className = '', priority = false }) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (!parallax) return;
    const onScroll = () => {
      const el = ref.current; if (!el) return;
      const r = el.getBoundingClientRect();
      const center = r.top + r.height / 2;
      const vh = window.innerHeight;
      const dist = (center - vh / 2) / vh;
      setOffset(dist * parallax);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [parallax]);

  // Tone-driven gradient for the placeholder; once real photos drop in this whole
  // backdrop disappears behind <img>.
  const tones = {
    industrial: ['#2c2823', '#0e0c08'],
    sky:        ['#3d5b75', '#1d2e3f'],
    earth:      ['#5a4a35', '#2a2218'],
    plant:      ['#3a4538', '#1a201a'],
    studio:     ['#5a5650', '#2a2722'],
  };
  const [c1, c2] = tones[tone] || tones.industrial;
  const stripe = 'rgba(255,255,255,0.025)';
  const ink = 'rgba(255,255,255,0.5)';

  return (
    <div ref={ref} className={`enq-photo ${className}`} style={{
      position: 'relative',
      aspectRatio: aspect === 'auto' ? undefined : aspect,
      width: '100%',
      background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`,
      overflow: 'hidden',
      ...style,
    }}>
      {src ? (
        <img
          src={src} alt={caption || ''}
          srcSet={srcSm ? `${srcSm} 700w, ${src} 1400w` : undefined}
          sizes={srcSm ? '(max-width: 1100px) 100vw, 50vw' : undefined}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', objectPosition,
            transform: parallax ? `translateY(${offset}px) scale(1.06)` : 'none',
          }}
        />
      ) : (
        <>
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: `repeating-linear-gradient(135deg, ${stripe} 0 1px, transparent 1px 18px)`,
            transform: `translateY(${offset}px)`,
            transition: 'none',
          }} />
          {/* Subtle silhouette suggestion — industrial */}
          <svg viewBox="0 0 200 150" preserveAspectRatio="xMidYMax slice"
               width="100%" height="100%"
               style={{ position: 'absolute', inset: 0, opacity: 0.22 }}>
            <path d="M0 140 L25 100 L40 110 L55 80 L70 95 L90 70 L105 88 L125 65 L140 85 L160 75 L200 95 L200 150 L0 150 Z"
                  fill={ink} />
            <path d="M0 145 L40 120 L80 135 L130 110 L180 130 L200 122 L200 150 L0 150 Z"
                  fill={ink} opacity="0.5" />
          </svg>
          <div style={{
            position: 'absolute', bottom: 10, left: 14,
            fontFamily: '"Source Sans 3", sans-serif',
            fontSize: 10, fontWeight: 500,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.45)',
          }}>
            Photograph · {caption}
          </div>
        </>
      )}
      {overlay}
    </div>
  );
}

// ── Header ─────────────────────────────────────────────────────────────────
// Maps the current URL to the nav-item id it should highlight. Everything
// under /services (including /services/epcm and /services/rd) and the
// /capabilities/<id> detail pages all live under the "Services" nav item.
function activeNavId(pathname) {
  const seg = pathname === '/' ? '' : pathname.replace(/^\//, '').split('/')[0];
  if (seg === 'about') return 'about';
  if (seg === 'services' || seg === 'capabilities') return 'capabilities';
  if (seg === 'insights') return 'insights';
  if (seg === 'careers') return 'careers';
  return '';
}

function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);

  const items = [
    { id: 'about',        label: 'About' },
    { id: 'capabilities', label: 'Services' },
    // Insights (blog) hidden until there's approved content — routes still
    // build but are noindex + absent from the sitemap and all navigation.
    { id: 'careers',      label: 'Careers' },
  ];

  const route = activeNavId(pathname);
  const onHeroPage = pathname === '/';
  const transparent = onHeroPage && !scrolled;

  return (
    <header style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
      background: transparent ? 'color-mix(in oklch, var(--bg) 0%, transparent)' : 'color-mix(in oklch, var(--bg) 92%, transparent)',
      backdropFilter: transparent ? 'blur(0px) saturate(100%)' : 'blur(14px) saturate(150%)',
      WebkitBackdropFilter: transparent ? 'blur(0px) saturate(100%)' : 'blur(14px) saturate(150%)',
      borderBottom: `1px solid ${transparent ? 'transparent' : 'var(--hair)'}`,
      transition: 'border-color .25s',
      color: transparent ? '#ffffff' : 'var(--fg)',
    }}>
      <div style={{
        maxWidth: 1440, margin: '0 auto', padding: '18px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
      }}>
        <Link href="/" style={{
          appearance: 'none', border: 0, background: 'transparent', padding: 0,
          color: 'inherit', cursor: 'pointer', display: 'inline-flex',
        }}>
          <Logo size={34} variant={transparent ? 'light' : 'dark'} />
        </Link>
        <nav className="enq-desktop-nav" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {items.map((it) => {
            const active = route.split('/')[0] === it.id;
            return (
              <Link key={it.id} href={routeToUrl(it.id)} style={{
                appearance: 'none', border: 0, background: 'transparent',
                padding: '8px 16px', cursor: 'pointer',
                fontFamily: '"Source Sans 3", sans-serif',
                fontSize: 15, fontWeight: 500,
                color: 'inherit', textDecoration: 'none',
                opacity: active ? 1 : 0.78,
                position: 'relative',
                whiteSpace: 'nowrap',
                transition: 'opacity .15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = active ? '1' : '0.78'; }}
              >
                {it.label}
                {active && <span style={{
                  position: 'absolute', left: 16, right: 16, bottom: 0, height: 2,
                  background: 'var(--accent)',
                }} />}
              </Link>
            );
          })}
        </nav>
        <div className="enq-desktop-cta" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link href="/contact" style={{
            appearance: 'none', border: `1px solid ${transparent ? 'rgba(255,255,255,0.3)' : 'var(--hair-s)'}`,
            background: transparent ? 'rgba(255,255,255,0.08)' : 'transparent',
            color: 'inherit', textDecoration: 'none', display: 'inline-block',
            padding: '10px 18px', cursor: 'pointer',
            fontFamily: '"Source Sans 3", sans-serif', fontSize: 14, fontWeight: 500,
            backdropFilter: transparent ? 'blur(8px)' : 'none',
            transition: 'background .2s',
          }}>
            Contact us →
          </Link>
        </div>
        <button onClick={() => setMenuOpen((v) => !v)} className="enq-mobile-toggle" style={{
          appearance: 'none', border: '1px solid currentColor', background: 'transparent',
          color: 'inherit', padding: '8px 12px',
          fontFamily: '"Source Sans 3", sans-serif', fontSize: 13, fontWeight: 500,
          cursor: 'pointer', display: 'none',
        }}>
          {menuOpen ? 'Close' : 'Menu'}
        </button>
      </div>
      {menuOpen && (
        <div className="enq-mobile-menu" style={{
          background: 'var(--bg)', color: 'var(--fg)',
          borderTop: '1px solid var(--hair)',
          padding: '12px 32px 18px', display: 'none', flexDirection: 'column', gap: 0,
        }}>
          {[...items, { id: 'contact', label: 'Contact' }].map((it) => (
            <Link key={it.id} href={routeToUrl(it.id)} onClick={() => setMenuOpen(false)} style={{
              appearance: 'none', border: 0, background: 'transparent', textAlign: 'left',
              padding: '14px 0', borderBottom: '1px solid var(--hair)',
              cursor: 'pointer', color: 'var(--fg)', textDecoration: 'none',
              fontFamily: '"Source Sans 3", sans-serif', fontSize: 18, fontWeight: 500,
            }}>{it.label}</Link>
          ))}
        </div>
      )}
    </header>
  );
}

// ── Section wrapper ────────────────────────────────────────────────────────
function Section({ id, children, style = {}, contained = true, py = true }) {
  return (
    <section id={id} style={{
      position: 'relative',
      padding: py ? 'var(--section-y) 0' : 0,
      ...style,
    }}>
      <div style={{
        maxWidth: contained ? 1320 : 'none',
        margin: '0 auto',
        padding: contained ? '0 32px' : 0,
        position: 'relative',
      }}>
        {children}
      </div>
    </section>
  );
}

// ── Button ─────────────────────────────────────────────────────────────────
function Button({ children, onClick, variant = 'solid', size = 'md', style = {}, icon = '→', type = 'button' }) {
  const sizes = {
    sm: { padX: 16, padY: 9,  fs: 14 },
    md: { padX: 20, padY: 12, fs: 15 },
    lg: { padX: 26, padY: 15, fs: 16 },
  }[size];
  const variants = {
    solid:   { bg: 'var(--accent)', fg: 'var(--on-accent)', border: 'var(--accent)' },
    outline: { bg: 'transparent', fg: 'var(--fg)', border: 'var(--hair-s)' },
    ghost:   { bg: 'transparent', fg: 'var(--accent)', border: 'transparent' },
    invert:  { bg: 'var(--fg)', fg: 'var(--bg)', border: 'var(--fg)' },
    light:   { bg: '#ffffff', fg: '#15171a', border: '#ffffff' },
  }[variant];
  return (
    <button type={type} onClick={onClick} style={{
      appearance: 'none', border: `1px solid ${variants.border}`,
      background: variants.bg, color: variants.fg,
      padding: `${sizes.padY}px ${sizes.padX}px`,
      fontFamily: '"Source Sans 3", sans-serif',
      fontSize: sizes.fs, fontWeight: 600,
      cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: 12,
      whiteSpace: 'nowrap',
      transition: 'transform .15s, background .15s, border-color .15s',
      ...style,
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>
      {children}
      {icon && <span aria-hidden style={{ display: 'inline-block', transition: 'transform .2s', fontSize: sizes.fs * 0.9 }}>{icon}</span>}
    </button>
  );
}

// ── ReadMore — text link with arrow ────────────────────────────────────────
function ReadMore({ children = 'Read more', onClick, style = {}, color }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick}
            onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
            style={{
      appearance: 'none', border: 0, background: 'transparent', padding: 0,
      cursor: 'pointer',
      fontFamily: '"Source Sans 3", sans-serif', fontSize: 14, fontWeight: 600,
      letterSpacing: '0.02em',
      color: color || 'var(--fg)',
      display: 'inline-flex', alignItems: 'center', gap: 10,
      whiteSpace: 'nowrap',
      ...style,
    }}>
      <span style={{
        borderBottom: `1px solid ${h ? 'var(--accent)' : 'currentColor'}`,
        paddingBottom: 2,
        transition: 'border-color .2s',
        whiteSpace: 'nowrap',
      }}>{children}</span>
      <span style={{
        color: 'var(--accent)',
        transform: h ? 'translateX(4px)' : 'translateX(0)',
        transition: 'transform .2s',
      }}>→</span>
    </button>
  );
}

// ── Footer ─────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer style={{
      background: 'var(--bg2)', color: 'var(--fg)',
      borderTop: '1px solid var(--hair)',
      padding: '80px 0 32px',
    }}>
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 32px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) repeat(3, minmax(0, 1fr))',
          gap: 48, paddingBottom: 56,
        }} className="enq-footer-grid">
          <div>
            <Logo size={40} variant="black" />
            <p style={{
              marginTop: 22, fontSize: 15, lineHeight: 1.6,
              color: 'var(--fg2)', maxWidth: 340,
            }}>
              A multi-disciplinary engineering, project delivery and technology
              development partner for the mining, minerals and industrial sectors.
            </p>
            <div style={{ marginTop: 24 }}>
              <a href="https://www.linkedin.com/company/engnza" target="_blank" rel="noreferrer" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                color: 'var(--fg2)', textDecoration: 'none', fontSize: 14, fontWeight: 500,
              }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor"><path d="M3.5 6.5h2.7v8.3H3.5zM4.85 2.5a1.55 1.55 0 110 3.1 1.55 1.55 0 010-3.1zM7.8 6.5h2.6v1.13c.37-.66 1.27-1.38 2.6-1.38 2.78 0 3.3 1.7 3.3 4.2v4.4h-2.7v-3.9c0-.93 0-2.1-1.36-2.1s-1.57.97-1.57 2.05v3.95h-2.7V6.5z"/></svg>
                LinkedIn
              </a>
            </div>
          </div>
          {[
            { title: 'Services', links: [
              ['EPCM delivery',    'capabilities/epcm'],
              ['R&D & innovation', 'capabilities/rd'],
              ['All services',     'capabilities'],
            ]},
            { title: 'Company', links: [
              ['About',     'about'],
              ['Contact',   'contact'],
            ]},
            { title: 'Offices', plain: [
              'Centurion · South Africa',
              'St Gallen · Switzerland',
            ]},
          ].map((col, i) => (
            <div key={i}>
              <div style={{
                fontFamily: '"Source Sans 3", sans-serif', fontSize: 12, fontWeight: 700,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                color: '#5a616a', // darkened from var(--mid): #6c727b on this footer's cream bg was ~4.4:1, just under WCAG AA's 4.5:1 for normal text
              }}>{col.title}</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '18px 0 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {col.links?.map(([label, target]) => (
                  <li key={label + target}>
                    <Link href={routeToUrl(target)} style={{
                      appearance: 'none', border: 0, background: 'transparent', padding: 0,
                      cursor: 'pointer', color: 'var(--fg)', fontSize: 15, textAlign: 'left',
                      textDecoration: 'none',
                      fontFamily: '"Source Sans 3", sans-serif', fontWeight: 400,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--fg)'; }}
                    >{label}</Link>
                  </li>
                ))}
                {col.plain?.map((line, j) => (
                  <li key={j} style={{ color: 'var(--fg2)', fontSize: 14, lineHeight: 1.5 }}>{line}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div style={{
          borderTop: '1px solid var(--hair)', paddingTop: 24,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 16,
          fontSize: 13, color: '#5a616a', // see note above — darkened from var(--mid) for WCAG AA contrast on this cream footer bg
        }}>
          <span>© 2026 Enginuity (Pty) Ltd · All rights reserved</span>
          <div style={{ display: 'flex', gap: 24 }}>
            {/* Privacy / Terms links hidden until the legal copy is client-approved. */}
            <a href="/sitemap.xml" style={{ color: 'inherit', textDecoration: 'none' }}
               onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent)'; }}
               onMouseLeave={(e) => { e.currentTarget.style.color = 'inherit'; }}>Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── PageHero — inner-page hero with a photo band ───────────────────────────
// ── Breadcrumbs ─────────────────────────────────────────────────────────
// Derives a trail from the current URL alone — no prop-threading needed
// from route files — by looking up human labels for dynamic segments
// (capability id, insight slug, category slug) against the same data
// modules the pages themselves render from. Renders nothing on routes it
// can't confidently label (falls through to just the segment text) or on
// '/' (home has no PageHero, so no breadcrumb strip either).
const STATIC_LABELS = {
  about: 'About',
  services: 'Services',
  epcm: 'EPCM',
  rd: 'R&D',
  careers: 'Careers',
  contact: 'Contact',
  insights: 'Insights',
  work: 'Projects',
  markets: 'Markets',
  faq: 'FAQ',
  privacy: 'Privacy Policy',
  terms: 'Terms & Conditions',
  capabilities: 'Services', // /capabilities/<id> nests under Services in the breadcrumb, matching the site's actual services taxonomy
  category: null, // structural only — real label comes from the following segment
};

// The "capabilities" URL segment is only ever a prefix — /capabilities/<id>
// pages exist, but /capabilities itself is not a route. Its breadcrumb
// crumb points at the real services landing page instead.
const CRUMB_PATH_OVERRIDE = { '/capabilities': '/services' };

function deriveBreadcrumbTrail(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [];
  const trail = [];
  let pathSoFar = '';
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    pathSoFar += `/${seg}`;
    if (seg === 'category') continue; // skip as its own crumb; next segment carries the real label

    let label = STATIC_LABELS[seg];
    if (label === undefined) {
      if (segments[i - 1] === 'capabilities') {
        label = CAPABILITIES.find((c) => c.id === seg)?.label || seg;
      } else if (segments[i - 1] === 'category') {
        label = getCategoryBySlug(seg)?.name || seg;
        pathSoFar = `/insights/category/${seg}`;
      } else if (segments[i - 1] === 'insights') {
        label = getInsightBySlug(seg)?.title || seg;
      } else {
        label = seg.charAt(0).toUpperCase() + seg.slice(1);
      }
    }
    if (label) trail.push({ name: label, path: CRUMB_PATH_OVERRIDE[pathSoFar] || pathSoFar });
  }
  return trail;
}

function Breadcrumbs({ trail }) {
  const pathname = usePathname();
  const items = trail || deriveBreadcrumbTrail(pathname);
  if (items.length === 0) return null;
  const full = [{ name: 'Home', path: '/' }, ...items];
  return (
    <nav aria-label="Breadcrumb" style={{ background: 'var(--bg2)', borderBottom: '1px solid var(--hair)' }}>
      <div style={{
        maxWidth: 1320, margin: '0 auto', padding: '14px 32px',
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
        fontSize: 13.5, color: '#5a616a', // AA contrast on the cream --bg2 (var(--mid) is ~4.4:1 here)
      }}>
        {full.map((it, i) => {
          const isLast = i === full.length - 1;
          return (
            <span key={it.path} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {i > 0 && <span aria-hidden="true" style={{ color: '#8a8f97' }}>/</span>}
              {isLast ? (
                <span aria-current="page" style={{ color: 'var(--fg)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '40ch' }}>{it.name}</span>
              ) : (
                <Link href={it.path} style={{ color: '#5a616a', textDecoration: 'none' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#5a616a'; }}>
                  {it.name}
                </Link>
              )}
            </span>
          );
        })}
      </div>
    </nav>
  );
}

function PageHero({ eyebrow, title, sub, tone = 'industrial', img, shift = false, children, breadcrumb }) {
  return (
    <>
      <section style={{
        position: 'relative',
        paddingTop: 0,
        background: '#0f1114',
        color: '#ffffff',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          <Photo aspect="auto" tone={tone} src={img} parallax={-30} style={{ height: '100%' }} caption={title} priority />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(15,17,20,0.6) 0%, rgba(15,17,20,0.4) 50%, rgba(15,17,20,0.85) 100%)' }} />
        </div>
        <div className={shift ? 'enq-hero-shift' : undefined} style={{
          position: 'relative',
          maxWidth: 1320, margin: '0 auto',
          padding: '180px 32px 88px',
        }}>
          {eyebrow && <Eyebrow color="#ffffff" style={{ opacity: 0.9 }}>{eyebrow}</Eyebrow>}
          <Headline size="xl" as="h1" style={{ color: '#ffffff', marginTop: 20, maxWidth: '18ch' }}>{title}</Headline>
          {sub && (
            <p style={{
              marginTop: 24, fontSize: 'clamp(17px, 1.3vw, 21px)', lineHeight: 1.5,
              color: 'rgba(255,255,255,0.85)', maxWidth: '54ch', margin: '24px 0 0',
            }}>{sub}</p>
          )}
          {children}
        </div>
      </section>
      <Breadcrumbs trail={breadcrumb} />
    </>
  );
}

// ── Form delivery ──────────────────────────────────────────────────────────
// One local endpoint (public/api/send-form.php) handles every form on the
// site — no third-party form service. `fields` is a plain object of
// form-field-name -> value; pass `file` (a File object) for the careers
// CV upload. Returns { ok, message, field } — `field` is set when the
// server rejected one specific input, so the caller can show an inline
// error instead of a generic failure.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value) {
  return EMAIL_RE.test(String(value || '').trim());
}

async function sendEnginuityForm({ formType, fields, file }) {
  try {
    const fd = new FormData();
    fd.append('form_type', formType);
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v ?? ''));
    if (file) fd.append('cv', file, file.name);
    const r = await fetch('/api/send-form.php', { method: 'POST', body: fd });
    const data = await r.json().catch(() => ({}));
    return {
      ok: r.ok && data.success === true,
      message: data.message || (r.ok ? 'Thanks — we will be in touch.' : 'Something went wrong. Please try again or email us directly.'),
      field: data.field,
    };
  } catch (err) {
    return { ok: false, message: 'Something went wrong sending your message. Please email us directly instead.' };
  }
}

// Hidden honeypot field: a real visitor never sees or fills it (moved off
// -screen, not display:none, so it still reads as a normal field to a
// screen reader-less bot but doesn't cost anything visually); the PHP
// handler silently no-ops any submission where it's non-empty.
function Honeypot() {
  return (
    <input
      type="text" name="website" tabIndex={-1} autoComplete="off"
      aria-hidden="true"
      style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
    />
  );
}

// ── Field — labelled form input, shared by every form on the site ─────────
function Field({ label, value, onChange, type = 'text', multiline = false, required = false, error, name }) {
  const [focus, setFocus] = useState(false);
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ fontSize: 13, color: 'var(--mid)', fontWeight: 600 }}>
        {label}{required && <span style={{ color: 'var(--accent)' }}> *</span>}
      </span>
      <Tag
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        required={required}
        rows={multiline ? 5 : undefined}
        aria-invalid={error ? 'true' : undefined}
        style={{
          appearance: 'none',
          border: `1px solid ${error ? '#b8462f' : focus ? 'var(--accent)' : 'var(--hair-s)'}`,
          background: 'var(--bg)', color: 'var(--fg)',
          fontFamily: '"Source Sans 3", sans-serif',
          fontSize: 15, padding: '12px 14px',
          outline: 'none',
          resize: multiline ? 'vertical' : 'none',
          transition: 'border-color .2s',
        }}
      />
      {error && <span style={{ fontSize: 13, color: '#b8462f' }}>{error}</span>}
    </label>
  );
}

export {
  Logo, Eyebrow, Headline, Photo, Header, Section,
  Button, ReadMore, Footer, PageHero, Breadcrumbs,
  sendEnginuityForm, isValidEmail, Honeypot, Field,
};
